import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import { courtTypesApi, extractOne } from '../../api/facades';

const CourtTypeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const numId = id ? parseInt(id, 10) : NaN;
  const [showDelete, setShowDelete] = React.useState(false);

  const { data: res, isLoading, isError } = useQuery({
    queryKey: ['court-types', numId],
    queryFn: () => courtTypesApi.get(numId),
    enabled: !Number.isNaN(numId),
  });
  const row = extractOne(res);

  const deleteMu = useMutation({
    mutationFn: () => courtTypesApi.remove(numId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['court-types'] });
      setShowDelete(false);
      navigate('/dashboard/settings/court-types');
      toast.success('Court deleted.');
    },
    onError: (e: any) => toast.error(e?.message || 'Delete failed.'),
  });

  if (Number.isNaN(numId) || isError || (!isLoading && !row)) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <p className="text-slate-600 dark:text-slate-400">Court not found.</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/dashboard/settings/court-types')}>Back</Button>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-slate-700 dark:text-slate-200">{isLoading ? 'Loading…' : (row as any)?.court_type_name ?? 'Court'}</h1>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => navigate('/dashboard/settings/court-types')}>Back</Button>
          <Button variant="primary" onClick={() => navigate(`/dashboard/settings/court-types/${id}/edit`)}>Edit</Button>
          <Button variant="secondary" onClick={() => setShowDelete(true)} className="text-red-600 hover:text-red-700">Delete</Button>
        </div>
      </div>
      {isLoading ? <p className="text-slate-500">Loading…</p> : (
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <dt className="text-xs font-semibold text-slate-500 uppercase">Active</dt>
            <dd className="text-slate-700 dark:text-slate-200">{(row as any)?.is_active ?? '—'}</dd>
          </div>
        </dl>
      )}
      <ConfirmDeleteModal open={showDelete} onClose={() => setShowDelete(false)} onConfirm={() => deleteMu.mutate()} title="Delete court" message="Are you sure? This cannot be undone." loading={deleteMu.isPending} />
    </div>
  );
};

export default CourtTypeDetailPage;
